import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BookHeart, Calendar as CalendarIcon, Pencil, Trash2, Edit3 } from 'lucide-react';
import { Button } from '../components/Button';
import { supabase } from '../supabase';
type Mood = 1 | 2 | 3 | 4 | 5;
interface JournalEntry {
  id: string;
  created_at: string;
  mood: Mood;
  content: string;
}
const moods = [{
  value: 1,
  emoji: '😢'
}, {
  value: 2,
  emoji: '🙁'
}, {
  value: 3,
  emoji: '😐'
}, {
  value: 4,
  emoji: '🙂'
}, {
  value: 5,
  emoji: '😄'
}];
export function Jurnal() {
  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);
  const [content, setContent] = useState('');
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // LOAD DATA SUPABASE
  useEffect(() => {
    const fetchData = async () => {
      const {
        data: userData
      } = await supabase.auth.getUser();
      const user = userData.user;
      if (!user) return;
      const {
        data
      } = await supabase.from('journal_entries').select('*').eq('user_id', user.id).order('created_at', {
        ascending: false
      });
      if (data) setEntries(data);
    };
    fetchData();
  }, []);

  // CREATE / UPDATE
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMood || !content.trim()) return;
    setIsSubmitting(true);
    const {
      data: userData
    } = await supabase.auth.getUser();
    const user = userData.user;
    if (!user) return;
    if (editingId) {
      const {
        data
      } = await supabase.from('journal_entries').update({
        mood: selectedMood,
        content: content.trim()
      }).eq('id', editingId).select();
      if (data) {
        setEntries(prev => prev.map(e => e.id === editingId ? data[0] : e));
      }
      setEditingId(null);
    } else {
      const {
        data
      } = await supabase.from('journal_entries').insert([{
        user_id: user.id,
        mood: selectedMood,
        content: content.trim()
      }]).select();
      if (data) setEntries([data[0], ...entries]);
    }
    setSelectedMood(null);
    setContent('');
    setIsSubmitting(false);
  };

  // DELETE
  const handleDelete = async (id: string) => {
    await supabase.from('journal_entries').delete().eq('id', id);
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  // EDIT
  const handleEdit = (entry: JournalEntry) => {
    setEditingId(entry.id);
    setSelectedMood(entry.mood);
    setContent(entry.content);
  };

  // 🔥 FINAL FORMAT (1 LINE: Hari, tanggal • jam.menit)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const timePart = date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${datePart} • ${timePart}`;
  };
  const getMood = (m: number) => moods.find(x => x.value === m)?.emoji || '😐';
  return <div className="min-h-screen bg-pastel-grey-light/30 py-10 px-4" data-id="element-298">

      <div className="max-w-5xl mx-auto" data-id="element-299">

        {/* HEADER */}
        <div className="text-center mb-10" data-id="element-300">
          <div className="w-14 h-14 mx-auto bg-pastel-pink-light rounded-2xl flex items-center justify-center mb-4" data-id="element-301">
            <BookHeart className="w-7 h-7 text-pink-600" data-id="element-302" />
          </div>
          <h1 className="text-3xl font-bold" data-id="element-303">Jurnal Harian</h1>
          <p className="text-gray-500 text-sm mt-2" data-id="element-304">
            Catat suasana hati kamu
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-305">

          {/* FORM */}
          <div className="bg-white rounded-3xl p-5 border shadow-sm" data-id="element-306">
            <h2 className="font-semibold mb-4 flex items-center gap-2" data-id="element-307">
              <Edit3 size={16} data-id="element-308" />
              {editingId ? 'Edit Jurnal' : 'Bagaimana Perasaanmu Hari ini?'}
            </h2>

            <div className="flex gap-2 mb-4" data-id="element-309">
              {moods.map(m => <button key={m.value} onClick={() => setSelectedMood(m.value as Mood)} className={`text-2xl p-2 rounded-lg transition ${selectedMood === m.value ? 'bg-pastel-blue-light' : 'hover:bg-gray-100'}`} data-id="element-310">
                  {m.emoji}
                </button>)}
            </div>

            <textarea className="w-full border rounded-xl p-3 min-h-[120px]" placeholder="Hari ini aku merasa..." value={content} onChange={e => setContent(e.target.value)} data-id="element-311" />

            <Button className="w-full mt-3" onClick={handleSubmit} isLoading={isSubmitting} data-id="element-312">
              {editingId ? 'Update Jurnal' : 'Simpan Jurnal'}
            </Button>

            {editingId && <button className="text-xs mt-2 text-gray-500 underline" onClick={() => {
            setEditingId(null);
            setSelectedMood(null);
            setContent('');
          }} data-id="element-313">
                batal edit
              </button>}
          </div>

          {/* LIST */}
          <div className="lg:col-span-2 space-y-4" data-id="element-314">

            {entries.length === 0 ? <div className="bg-white p-10 text-center rounded-3xl border" data-id="element-315">
                Belum ada jurnal
              </div> : entries.map(entry => <motion.div key={entry.id} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="bg-white rounded-3xl p-5 border shadow-sm" data-id="element-316">

                  <div className="flex justify-between mb-3" data-id="element-317">

                    <div className="flex items-start gap-3" data-id="element-318">

                      <span className="text-3xl" data-id="element-319">
                        {getMood(entry.mood)}
                      </span>

                      {/* FINAL SINGLE LINE DATETIME */}
                      <div className="text-xs text-gray-400 flex items-center gap-1" data-id="element-320">
                        <CalendarIcon size={12} data-id="element-321" />
                        {formatDate(entry.created_at)}
                      </div>

                    </div>

                    {/* ACTION */}
                    <div className="flex gap-2" data-id="element-322">

                      <button onClick={() => handleEdit(entry)} className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition" data-id="element-323">
                        <Pencil size={14} data-id="element-324" />
                      </button>

                      <button onClick={() => handleDelete(entry.id)} className="p-2 rounded-lg bg-pastel-pink-light text-pink-600 hover:bg-pink-200 transition" data-id="element-325">
                        <Trash2 size={14} data-id="element-326" />
                      </button>

                    </div>

                  </div>

                  <p className="text-gray-700 text-sm whitespace-pre-wrap" data-id="element-327">
                    {entry.content}
                  </p>

                </motion.div>)}

          </div>

        </div>
      </div>
    </div>;
}